import {
  Box,
  Grid as ChakraGrid,
  Hide,
  List,
  Show,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  Divider,
  ReviewStars,
  TextTruncate,
} from '@time-line/components/common';
import { ProductInfo } from 'components/reviews/ProductInfo';
import { ProductReviewMedia } from 'components/reviews/ProductReviewMedia';
import { formatDistance } from 'date-fns';
import { env } from 'lib/constants/env';
import type { JunipProductReview } from 'types/reviews/JunipProductReview';

import { ProductReviewAttributes } from '../ProductReviewAttributes';
import { ProductReviewResponse } from '../ProductReviewResponse';
import { ReviewUpDownVote } from '../ReviewUpDownVote';
import { VerifiedBuyerBadge } from '../VerifiedBuyerBadge';

interface ProductReviewProps {
  review: JunipProductReview;
  showProduct?: boolean;
  index: number;
}

const getDateDifference = (createdAt: Date) => {
  const today = new Date();
  const reviewDate = new Date(createdAt);

  return formatDistance(reviewDate, today, {
    addSuffix: true,
  });
};

const UPVOTE_WIDGET_SETTING = env.NEXT_PUBLIC_REVIEWS_UPVOTE_WIDGET;

export function ProductReview({
  review,
  showProduct,
  index,
}: ProductReviewProps) {
  const {
    id,
    rating,
    createdAt,
    customer,
    title,
    body,
    photoUrls,
    videoUrls,
    verifiedBuyer,
    wouldRecommend,
    attributeRecords,
    response,
    upVoteCount,
    downVoteCount,
  } = review;

  const mediaUrls = [...(photoUrls || []), ...(videoUrls || [])];

  const customerName = customer?.firstName
    ? `${customer?.firstName}${
        customer?.lastName ? ` ${customer.lastName}.` : ''
      }`
    : null;

  return (
    <ChakraGrid templateColumns="repeat(12, 1fr)" pt="m" gap="m">
      <ChakraGrid
        as="blockquote"
        gridColumn={{ base: 'span 12', lg: 'span 4' }}
        gridTemplateColumns={{ base: '1fr 1fr', lg: 'initial' }}
        gap="2xs"
      >
        <Stack gap="2xs">
          {rating && (
            <Hide below="lg">
              <ReviewStars score={rating} />
            </Hide>
          )}
          {(customerName || verifiedBuyer) && (
            <Box>
              {customerName && (
                <Text as="cite" size="detail">
                  {customerName}
                </Text>
              )}
              {verifiedBuyer && <VerifiedBuyerBadge />}
            </Box>
          )}
          {(wouldRecommend || attributeRecords) && (
            <List spacing="2px" listStyleType="none">
              {wouldRecommend && (
                <Text as="li" size="detail" fontWeight="medium">
                  Yes, I would recommend this product
                </Text>
              )}
              {attributeRecords && (
                <ProductReviewAttributes attributeRecords={attributeRecords} />
              )}
            </List>
          )}
        </Stack>
        {showProduct && (
          <Box alignSelf={{ lg: 'end' }}>
            <ProductInfo review={review} />
          </Box>
        )}
      </ChakraGrid>
      <Box gridColumn={{ base: 'span 12', lg: 'span 8' }}>
        <ChakraGrid
          gridTemplateColumns={{ base: '1fr 1fr', lg: 'initial' }}
          alignItems={{ base: 'center', lg: 'start' }}
          mb="4xs"
          gap={{ base: '4xs', lg: '2xs' }}
        >
          {rating && (
            <Show below="lg">
              <ReviewStars score={rating} />
            </Show>
          )}
          {title && (
            <Text
              as="h3"
              size="title2"
              gridColumn={{ base: 'span 2', lg: 1 }}
              flexGrow={1}
            >
              {title}
            </Text>
          )}
          {createdAt && (
            <Text
              size="detail"
              color="blackAlpha.700"
              textAlign="right"
              flexShrink={0}
              gridColumn={2}
              gridRow={1}
              pt={{ lg: '5xs' }}
            >
              {getDateDifference(createdAt)}
            </Text>
          )}
        </ChakraGrid>
        <Stack gap="sm">
          {body && (
            <Box>
              <TextTruncate size="body2" color="blackAlpha.700" noOfLines={4}>
                {body}
              </TextTruncate>
            </Box>
          )}
          {mediaUrls.length > 0 && (
            <ProductReviewMedia id={`review-${index}`} mediaUrls={mediaUrls} />
          )}
          {(UPVOTE_WIDGET_SETTING === 'ALL' ||
            UPVOTE_WIDGET_SETTING === 'UP') && (
            <ReviewUpDownVote
              upVoteCount={upVoteCount}
              downVoteCount={downVoteCount}
              reviewId={id}
              isDownVoteVisible={UPVOTE_WIDGET_SETTING === 'ALL'}
              order={{ base: 99999, md: 0 }}
            />
          )}
          {response && <ProductReviewResponse response={response} />}
        </Stack>
      </Box>
      <Divider gridColumn="span 12" addOpacity />
    </ChakraGrid>
  );
}
